<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed inset-0 overflow-hidden z-50"
            @close="open = false"
            :open="open"
        >
            <div
                id="product-recommendation-side-bar"
                class="absolute inset-0 overflow-hidden"
            >
                <DialogOverlay class="absolute inset-0" />
                <div class="fixed inset-y-0 pl-8 max-w-full right-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transform transition ease-in-out duration-500"
                        enter-from="translate-x-full"
                        enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500"
                        leave-from="translate-x-0"
                        leave-to="translate-x-full"
                    >
                        <div class="w-screen max-w-md">
                            <div
                                class="border-l border-gray-200 h-full flex flex-col bg-white shadow-xl"
                            >
                                <div
                                    :class="[
                                        cartIsEmpty
                                            ? 'bg-white'
                                            : 'bg-nutreance-green',
                                        'py-3 px-4 sm:px-6',
                                    ]"
                                >
                                    <div
                                        class="flex items-center justify-between"
                                    >
                                        <DialogTitle
                                            class="text-lg font-medium text-white"
                                        >
                                            <div
                                                v-if="!cartIsEmpty"
                                                class="flex items-center"
                                            >
                                                <ProductIcon
                                                    class="focus:outline-none"
                                                    :imgClass="'w-6'"
                                                    :product="product"
                                                />
                                                <CheckIcon
                                                    class="h-6 w-6 ml-4 mr-2"
                                                />
                                                {{ quantityAdded }}
                                                {{ product.name }} Added to Cart
                                            </div>
                                        </DialogTitle>
                                        <div class="ml-3 h-7 flex items-center">
                                            <button
                                                type="button"
                                                :class="[
                                                    cartIsEmpty
                                                        ? 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                                                        : 'text-white bg-nutreance-green hover:bg-nutreance-green-darker',
                                                    'p-1 rounded-md focus:outline-none',
                                                ]"
                                                @click="open = false"
                                            >
                                                <span class="sr-only"
                                                    >Close panel</span
                                                >
                                                <XIcon
                                                    class="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="!cartIsEmpty"
                                    class="flex-1 h-0 overflow-y-auto"
                                >
                                    <div
                                        class="flex flex-col items-center border-b border-gray-200"
                                    >
                                        <div class="w-full px-4 sm:px-12 pt-3">
                                            <div
                                                class="flex items-baseline justify-between"
                                            >
                                                <dt
                                                    class="font-bold text-gray-500 text-md"
                                                >
                                                    Total ({{
                                                        `${state.cartQuantity} ${state.cartQuantity === 1 ? "Item" : "Items"}`
                                                    }}):
                                                </dt>
                                                <dd
                                                    class="font-semibold text-md text-gray-800"
                                                >
                                                    ${{
                                                        dollarsForDisplay(
                                                            state.cartTotals
                                                                .total,
                                                        )
                                                    }}
                                                </dd>
                                            </div>

                                            <div
                                                v-if="
                                                    state.countryCode !== 'US'
                                                "
                                                class="mt-2 text-right text-xs text-gray-500"
                                            >
                                                <span
                                                    >* All prices in USD.</span
                                                >
                                            </div>
                                        </div>

                                        <div class="w-full shrink-0 px-4 py-2">
                                            <a
                                                role="button"
                                                data-test="continue-to-cart"
                                                class="w-full flex justify-center px-6 py-3 my-2 border border-gray-300 shadow text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                                href="/cart"
                                            >
                                                <span>Continue to Cart</span>
                                                <ArrowSmRightIcon
                                                    class="h-6 w-6 ml-2"
                                                />
                                            </a>
                                        </div>
                                    </div>

                                    <slot name="recommendation" />

                                    <RecommendedProductOrderForm
                                        :headquarters="headquarters"
                                        :product="product"
                                        :recommendedProduct="recommendedProduct"
                                        :subscriptionOptionId="
                                            subscriptionOptionId
                                        "
                                    />
                                </div>

                                <div v-else class="flex-1 h-0 overflow-y-auto">
                                    <div
                                        class="mt-6 flex-1 flex flex-col justify-between"
                                    >
                                        <div class="px-4 sm:px-6">
                                            <div class="py-48 text-center">
                                                <ShoppingCartIcon
                                                    class="mx-auto h-12 w-12 text-gray-400"
                                                />
                                                <p
                                                    class="mt-2 font-medium text-gray-700"
                                                >
                                                    Your cart is empty.
                                                </p>
                                                <div class="mt-6">
                                                    <a
                                                        role="button"
                                                        class="inline-flex justify-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                                                        href="/product-categories/all-products"
                                                    >
                                                        <SearchIcon
                                                            class="h-6 w-6 mr-2"
                                                        />
                                                        Browse Our Products
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { inject, onMounted, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { SearchIcon, ShoppingCartIcon, XIcon } from "@heroicons/vue/outline";
import { ArrowSmRightIcon, CheckIcon } from "@heroicons/vue/solid";
import { useMoney } from "../../functions/useMoney";
import { useCart } from "../../functions/useCart";
import ProductIcon from "./ProductIcon.vue";
import RecommendedProductOrderForm from "./RecommendedProductOrderForm.vue";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import { loadCart as loadCartRequest } from "../../api/cart";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    headquarters?: any;
    initialCartQuantity?: number;
    product?: any;
    recommendedProduct?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { dollarsForDisplay } = useMoney();
const { pushViewItemListToDataLayer } = useGoogleDataLayer();
const { state, cartIsEmpty, onCartItemUpdated } = useCart();
const open = ref(false);
const quantityAdded = ref(1);
const subscriptionOptionId = ref<number | null>(null);

loadCart();

onMounted(() => {
    emitter.on("openProductRecommendationSideBar", (data: any) => {
        open.value = true;
        quantityAdded.value = data.quantityAdded;
        subscriptionOptionId.value = data.subscriptionOptionId;

        pushViewItemListToDataLayer({
            products: [props.recommendedProduct],
            subscribe: !!data.subscriptionOptionId,
            source: `${props.product.name} Recommended Product Side Bar for ${props.recommendedProduct.name}`,
        });
    });

    emitter.on("cartItemUpdated", (data: any) => {
        onCartItemUpdated(data);
    });
});

function loadCart() {
    loadCartRequest().then((data) => {
        state.cart = data.cart;
        state.cartTotals = data.cartTotals;
        state.countryCode = data.countryCode;
        state.discounts = data.discounts;
        state.tips = data.tips;
    });
}
</script>
